// The home intro, one frame at time t (ms since it started). From the Jitter
// composition: the reel opens with three wipes, the side cards fly in and flip,
// then the reel button, the headline, the header, the menu button and the award.
import { CARDS, PERSPECTIVE, progress, put, type Pose } from "./motion"

export type Nodes = Record<string, HTMLElement | undefined>
export type Layout = { mobile: boolean; top: number; h: number; H: number; pose: (offset: number) => Pose }

export function drawIntro(n: Nodes, t: number, L: Layout, skipCards = false) {
    const transform = (key: string, v: string) => put(n[key], "transform", v)
    const opacity = (key: string, v: number) => put(n[key], "opacity", String(v))
    for (const key of ["button", "nav", "scroll"]) put(n[key], "pointerEvents", t >= 3000 ? "auto" : "none")

    // the reel: grows out of a rotated point, then settles into the centre tile
    const grow = progress(t, 660, 690, "opening")
    // phone: it opens smaller at screen centre, then grows into the 370x432 tile
    // while rising to its spot below the header
    const zoom = L.mobile
        ? t < 2000
            ? 0.62 + 0.1 * progress(t, 0, 2210, "opening")
            : 0.72 + 0.28 * progress(t, 2000, 1045)
        : t < 2000
          ? 0.9 + 0.4 * progress(t, 0, 2210, "opening")
          : 1.2982 - 0.2982 * progress(t, 2000, 1045)
    const rise = L.H / 2 - (L.top + L.h / 2)
    if (!skipCards) {
        transform(
            "reel",
            `translate(-50%,-50%) ${L.mobile ? `translateY(${rise * (1 - progress(t, 2000, 1045))}px) ` : ""}rotate(${-10 * (1 - grow)}deg) scale(${Math.max(0.00001, grow * zoom)})`
        )
        put(n.reel, "borderRadius", `${(L.mobile ? 16 : 22) * grow}px`)
    }
    // three wipes over the reel (images 3, 2, 1)
    ;[1140, 1390, 1600].forEach((start, i) => put(n["wipe" + i], "clipPath", `inset(${100 * (1 - progress(t, start, 700, "out"))}% 0 0 0)`))

    // four dots orbit, then run out to the reel's corners
    transform("dots", `rotate(${progress(t, 0, 1195, "smooth") * 360}deg)`)
    opacity("dots", 1 - progress(t, 850, 345, "linear"))
    const spread = 20 * progress(t, 0, 800)
    const corner = progress(t, 660, 690, "opening")
    const cornerX = L.mobile ? 140 : 367,
        cornerY = L.mobile ? (165 * L.h) / 432 : 178
    ;[
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
    ].forEach(([x, y], i) =>
        transform("dot" + i, `translate(${x * (spread + (cornerX - spread) * corner)}px,${y * (spread + (cornerY - spread) * corner)}px)`)
    )

    // side cards: Jitter's stagger, travel and separate flip timing, in the
    // carousel camera's depth space (on phones only the centre tile shows)
    if (!skipCards)
        CARDS.forEach((c, i) =>
            [-1, 1].forEach((side) => {
                const m = progress(t, c.move, i < 2 ? 1053 : 940)
                const pose = L.pose(side * (i + 1))
                const angle = -side * c.from + (pose.angle + side * c.from) * progress(t, c.flip, c.dur, "smooth")
                const cameraScale = 1 - pose.z / PERSPECTIVE
                const x = pose.x - side * 230 * cameraScale * (1 - m)
                const key = `card${side}_${i}`
                opacity(key, L.mobile ? 0 : i < 2 ? (t >= c.show ? 1 : 0) : progress(t, c.show, 90, "out"))
                put(n[key], "pointerEvents", "none")
                transform(key, `translate(-50%,-50%) translateX(${x}px) translateZ(${pose.z}px) rotateY(${angle}deg)`)
            })
        )

    // reel button: the white pill opens from its centre, then its label slides in
    const button = progress(t, 2000, 885, "opening")
    put(n.buttonSurface, "width", `${157 * button}px`)
    put(n.buttonSurface, "left", `${78.5 * (1 - button)}px`)
    opacity("button", t >= 2000 ? 1 : 0)
    if (L.mobile)
        put(n.button, "transform", `translate(-50%,-50%) translateY(${rise * (1 - progress(t, 2000, 1045))}px) scale(calc(1.27 * var(--inverse, 1)))`)
    const label = progress(t, 2420, 465, "out")
    const labelFade = progress(t, 2420, 465, "linear")
    transform("buttonText", `translate(-50%,-50%) translateX(${45 * (1 - label)}px)`)
    opacity("buttonText", labelFade)
    transform("buttonArrow", `translateX(${-45 * (1 - label)}px)`)
    opacity("buttonArrow", labelFade)

    // headline, line by line
    ;[0, 1, 2, 3].forEach((i) => {
        const p = progress(t, 2585 + 150 * i, 500, "out")
        transform("line" + i, `translateY(${100 * (1 - p)}%)`)
        opacity("line" + i, p)
    })

    // header (the shell's logo, signature and local time)
    ;[0, 1].forEach((i) => {
        const signature = progress(t, 3045 + 113 * i, 372, "out"),
            local = progress(t, 3053 + 113 * i, 372, "out")
        transform("signature" + i, `translateX(${-24 * (1 - signature)}px)`)
        opacity("signature" + i, t >= 3045 + 113 * i ? 1 : 0)
        // local time slides in from the right on desktop, from the left on phones
        transform("time" + i, `translateX(${(L.mobile ? -24 : 24) * (1 - local)}px)`)
        opacity("time" + i, t >= 3053 + 113 * i ? 1 : 0)
    })
    transform("logo", `translateX(${-30 * (1 - progress(t, 3045, 493, "out"))}px)`)
    opacity("logo", t >= 3045 ? 1 : 0)

    const scroll = progress(t, 2920, 520, "out")
    opacity("scroll", scroll)
    transform("scroll", `translateY(${-20.5 * (1 - scroll)}px)`)

    // menu button (the shell's): widens out of the edge while its icon turns
    const nav = progress(t, 2730, 800, "natural")
    if (n.nav) {
        put(n.nav, "width", `${(L.mobile ? 70 : 60) * nav}px`)
        put(n.nav, "transform", `translateX(${30 * (1 - nav)}px)`)
    }
    opacity("nav", progress(t, 2730, 430, "linear"))
    transform("navIcon", `rotate(${-90 * (1 - progress(t, 2730, 800, "natural"))}deg)`)
    opacity("navIcon", progress(t, 3045, 485, "linear"))

    const award = progress(t, 3683, 477, "out")
    transform("award", `translateX(${30 * (1 - award)}px)`)
    opacity("award", t >= 3683 ? 1 : 0)
}
